import React, { ChangeEvent, useEffect, useState, useRef, FormEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { Claim, ClaimStatus, UpdateClaim } from "../../models/Claim";
import restClient from "../../services/clients/AnonymousRestClient";
import { ELVData, FileType, FileUpload, PostELVData } from "../../models/ELVData";
import { CountryAutoComplete } from "../../models/Autocomplete";
import { IBANValidationResult } from "../../models/ValidationResult";
import authRestClient from "../../services/clients/AuthRestClient";
import { PostAddress } from "../../models/Address";
import { AxiosError, AxiosResponse } from "axios";
import { BadRequestError, FieldError } from "../../models/Errors/BadRequest";

type FormState = {
    currentStep : number
    error       : string
}

const PostBankDetails = () => {

    const { claimGuid } = useParams();
    const navigate = useNavigate();

    const fileInput = useRef<HTMLInputElement>(null);

    let elvDataInit: PostELVData = {
        claimId          : "",
        iban             : "",
        bic              : "",
        bankname         : "",
        accountHolderName: "",
        bankAddress: {
            street    : "",
            postalCode: "",
            city      : "",
            countryId : -1
        },
        accountHolderAddress: {
            street     : "",
            postalCode : "",
            city       : "",
            countryId  : -1
        },
        uploadedFiles: []
    };

    /* State Properties */
    const [claim, setClaim]               = useState<Claim>();
    const [confirmed, setConfirmed]       = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const [elvData, setElvData]     = useState<PostELVData>(elvDataInit);
    const [countries, setCountries] = useState<CountryAutoComplete[]>([]);

    const [formState, setFormState]     = useState<FormState>({ currentStep: 1, error: "" })
    const [fieldErrors, setFieldErrors] = useState<FieldError>({});
    const [sepa,  setSepa]              = useState<boolean>(false);
   
    /* Optional depending on sepa state */
    const [bankAddress, setBankAddress] = useState<PostAddress>({
        street     : "",
        postalCode : "",
        city       : "",
        countryId  : -1
    });

    /**
     * Method that handles form submit. Sends form data via axios instance to api endpoint to ad 
     * ELV Data for a specific claim form.
     * @param e
     * @returns
     */
    async function handleSubmit(): Promise<void>
    {
        let newElvData: ELVData | null = null;

        // #region Validation

        if (!confirmed)
        {
            setFormState(x => ({ ...x, error: "Please indicate that you have read and agree to the Terms and Conditions" }));
            return;
        }

        if (elvData.uploadedFiles.length === 0 || elvData.uploadedFiles.some(x => x.Type === FileType.Undefined))
        {
            setFormState(x => ({ ...x, error: "Please provide at least one document. Make sure you have selected a category for each document." }));
            return;
        }

        // #endregion

        /* Set Bankaddress details for posting to backend */
        elvData.bankAddress = sepa ? null : bankAddress;

        let formData: FormData = new FormData();

        formData.append("claimId", claim?.guid ?? "");

        elvData.uploadedFiles.forEach(item => {
            formData.append("files", item.File);
            formData.append("categories", item.Type.toString());
        })

        setIsSubmitting(true);

        authRestClient.post("/api/elvdata/uploadFiles", formData, { headers: { "Content-Type": "multipart/form-data" } })
            .then(response => {
                if (response.status === 200) {
                    authRestClient.post<ELVData>("/api/elvdata", elvData).then(elvResponse => onApiSuccess(elvResponse))
                        .catch(err => onApiError(err));
                }
            })
            .catch(onApiError)
            .finally(() => setIsSubmitting(false));
    }

    /**
     * Callback method that handles multiple axios responses
     * @param param0
     */
    const onApiSuccess = (elvDataResponse: AxiosResponse<ELVData, any>) => {

        if (elvDataResponse.status === 200) {
            /* Set Claim to Status 'Requested' */
            let payload: UpdateClaim = {
                guid     : elvData.claimId,
                confirmed: confirmed,
                status   : ClaimStatus.Requested
            }

            setFormState(x => ({ ...x, error: "" })); // Reset Error view when post has been successfully transmitted ...
            setIsSubmitting(true);

            restClient.put<Claim>("/api/travelclaim", payload)
                .then(response => {
                    /* OnSuccess redirect to Detail view for current Claim  */
                    navigate("/me/claims/" + claimGuid);
                })
                .catch(err => {
                    /* TODO: Proper Error Handling */
                })
                .finally(() => setIsSubmitting(false));
        }
    }

    /**
     * Callback method that handles AxiosError response and sets error states for 
     * the current react component.
     * @param err
     */
    const onApiError = (err: AxiosError) => {

        if (err.response!.status === 422) {
            if (err.response?.data && (err.response.data as string)) {
                setFormState(x => ({ ...x, error: err.response?.data as string }))
            }
        }
        else if (err.response!.status === 400) {
            /* Bad Request | Most likley caused by missing required field */
            if (err.response?.data && (err.response.data as BadRequestError)) {
                let errors = (err.response.data as BadRequestError).errors;
                setFieldErrors(errors);
            }
        }

        /* Add Endpoint Call for Rollback Method */
        authRestClient.delete("/api/elvdata/rollback" + elvData.claimId);
    }

    /**
     * Method that renders an error message when an incorrect IBAN has been entered. (OnBlur)
     * @param e
     */
    async function validateIBAN(e: React.FocusEvent<HTMLInputElement, Element>): Promise<void>
    {
        restClient.get<IBANValidationResult>("/api/elvdata/validate/" + elvData.iban)
            .then(response => {
                if (response.status === 200)
                {
                    setFormState(x => ({ ...x, error: response.data.isValid ? "" : response.data.errorMessage }));
                    setSepa(response.data.isValid  ? response.data.sepa : false);
                }
            })
            .catch(err => { // Some generic error handling ...
                setSepa(false);
            });
    }

    /**
     * Handling Next Button Click
     */
    function handleNextStep(e: FormEvent<HTMLFormElement>) {

        e.preventDefault();

        let errors: FieldError = {};

        /* Check Requirements | Validation required for input fields */

        if (elvData.accountHolderAddress.countryId  === -1) { errors["AccountHolderAddress.Country"] = ["Please select a country"] }
        if (!sepa && elvData.bankAddress?.countryId === -1) { errors["BankAddress.Country"]          = ["Please select a country"] }

        let hasValidationError: boolean = (Object.keys(errors).length > 0);

        if (!hasValidationError) {
            setFormState(x => ({ ...x, currentStep: 2 }))
        }
        else
        {
            setFieldErrors(errors);
        }
    }

    /**
     * Handling changes for File Upload. 
     * @param e
     * @param index
     */
    function handleFileAttributeChange(e: React.ChangeEvent<HTMLSelectElement>, index: number) {

        setElvData(x => {

            let files = [...x.uploadedFiles];
            let file = files[index];

            if (!file) return x; // Guard

            files[index] = {
                ...file, Type: Number(e.target.value) as FileType
            }

            return {
                ...x,
                uploadedFiles: files
            }
        })

    }

    /**
     * Handles new uploaded documents. Will add documents to table overview.
     * @param e: HTML Input that triggered the event.
     */
    function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) : void {

        if (!e.target.files) return;

        let fileToUpload: FileUpload[] = Array.from(e.target.files).map(x => {
            return {
                File: x,
                Type: FileType.Undefined
            } 
        })

        setElvData(x => ({ ...x, uploadedFiles: [...x.uploadedFiles, ...fileToUpload] }))
    }

    /**
     * Handles the removal of uploaded files. 
     * @param index
     */
    function handleFileRemove(index: number): void {

        let files = elvData.uploadedFiles.filter((item, i) => i !== index);
        setElvData(x => ({ ...x, uploadedFiles: files }));
    } 

    /**
     * 
     * @returns
     */
    function renderWarningPane(): React.JSX.Element {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="alert alert-warning" role="alert">
                        <i className="bi bi-exclamation-diamond me-2" />
                        Please upload all relevant travel supporting documents, such as train or plane tickets and accommodation supporting documents.
                        <ul className="mb-0">
                            <li>Maximum of 10 documents</li>
                            <li>Maximum file size: 10 MB per document</li>
                            <li>Allowed formats: <b>.jpg</b>, .jpeg, .png, .gif, .svg, <b>.pdf</b></li>
                        </ul>
                    </div>
                </div>
            </div>
        )        
    }

    /**
     * 
     * @returns
     */
    function renderHTMLFileUpload(): React.JSX.Element {
        return (
            <section>

                {
                    (elvData.uploadedFiles && elvData.uploadedFiles.length === 0 && renderWarningPane())
                }

                <div className="row">
                    <div className="col-12">

                        <table className="table">
                            <thead>
                                <tr>
                                    <th colSpan={2}>
                                        <label className="fw-bold">Supporting Documents Upload</label>
                                    </th>
                                    <th colSpan={2} className="text-end">
                                        <button className="btn btn-primary" onClick={e => { e.preventDefault(); if (!fileInput || !fileInput.current) return; fileInput.current.click(); }}><i className="bi bi-cloud-upload me-1" />Upload Document</button>
                                        <input type="file" id="files" ref={fileInput} accept="image/*,application/pdf" onChange={handleFileUpload} hidden />
                                    </th>
                                </tr>
                                <tr>
                                    <th>{ /* Empty for Image */}</th>
                                    <th>Filename</th>
                                    <th>Category</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    elvData.uploadedFiles.map((x, index) => {
                                        return (
                                            <tr key={"File_" + index}>
                                                <td>
                                                    <i style={{ fontSize: "24px" }} className="bi bi-file-earmark-text-fill"></i>
                                                </td>
                                                <td>
                                                    <input className="form-control" type="text" disabled value={x.File.name} />
                                                </td>
                                                <td>
                                                    <select className="form-select" onChange={e => handleFileAttributeChange(e, index)} defaultValue={x.Type}>
                                                        <option value={FileType.Undefined}>
                                                            Please select category
                                                        </option>
                                                        <option value={FileType.Travel}>
                                                            Travel
                                                        </option>
                                                        <option value={FileType.Accommodation}>
                                                            Accommodation
                                                        </option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <button className="btn btn-danger" onClick={e => handleFileRemove(index)}>
                                                        <i className="bi bi-x-lg"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })
                                }
                            </tbody>
                        </table>

                    </div>
                </div>

                <hr />

                <div className="row">
                    <div className="col-12">

                        <div className="input-group mb-3">
                            <div className="input-group-text">
                                <input className="form-check-input mt-0" type="checkbox" checked={confirmed} aria-label="Checkbox for following text input" onChange={e => setConfirmed(e.target.checked)} />
                            </div>
                            <span className="form-control">
                                I hereby confirm that I have read and accept <a target="_blank" rel="noreferrer" href="https://www.era.int/?100305&en">ERA's General Terms and Conditions</a> and that the information given is correct.
                            </span>
                        </div>

                    </div>
                </div>

                <hr className="mt-0" />

                <div className="row">
                    <div className="form-group col-6 col-12">
                        <button className="btn btn-primary w-100" onClick={e => setFormState(x => ({ ...x, currentStep: 1 }))} disabled={isSubmitting}>
                            <i className="bi bi-arrow-left me-1"></i> <b>Previous Step:</b> Account Informations
                        </button>
                    </div>

                    <div className="form-group col-6 col-12">
                        {isSubmitting
                            ? <button type="button" className="btn btn-outline-primary w-100" disabled={true}><img src="/Loading.gif" style={{ width: "32px" }} alt="imgLoading" /></button>
                            : <button className="btn btn-success w-100" onClick={handleSubmit}>Submit</button>
                        }
                    </div>
                </div>

            </section>
        )
    }

    /**
     * Functions renders HTML Form Element for submitting elvdata.
     * @returns JSX Element
     */
    function renderHTMLForm(): React.JSX.Element {
        return (
            <form onSubmit={handleNextStep}>
                <div className="row">

                    <div className="form-group col-12">
                        <label htmlFor="iban" className="fw-bold">IBAN</label>
                        <input id="iban" name="iban" className="form-control" required onChange={e => setElvData(x => ({ ...x, iban: e.target.value }))} onBlur={validateIBAN} value={elvData.iban} />
                        <span className="text-danger">{getErrorMessageforField("IBAN")}</span>
                    </div>
                    <div className="form-group col-12">
                        <label htmlFor="bic" className="fw-bold">BIC</label>
                        <input id="bic" name="bic" className="form-control" required onChange={e => setElvData(x => ({ ...x, bic: e.target.value }))} value={elvData.bic} />
                        <span className="text-danger">{getErrorMessageforField("BIC")}</span>
                    </div>

                    <hr />

                    { /* Bank Address Data */}

                    <div className="form-group col-12">
                        <label htmlFor="bankname" className="fw-bold">Bank Name</label>
                        <input id="bankname" name="bankname" className="form-control" required onChange={e => setElvData(x => ({ ...x, bankname: e.target.value }))} value={elvData.bankname} />
                        <span className="text-danger">{getErrorMessageforField("Bankname")}</span>
                    </div>

                    {!sepa && (
                        <>
                            <div className="form-group col-12">
                                <label htmlFor="bankStreet" className="fw-bold">Street</label>
                                <input id="bankStreet" name="bankStreet" required className="form-control" onChange={e => setBankAddress(x => ({ ...x, street: e.target.value }))} value={bankAddress.street} />
                                <span className="text-danger">{getErrorMessageforField("BankAddress.Street")}</span>
                            </div>
                            <div className="form-group col-12 col-md-4">
                                <label htmlFor="bankPostalCode" className="fw-bold">Postal Code</label>
                                <input type="text" name="bankPostalCode" required id="bankPostalCode" className="form-control" onChange={e => setBankAddress(x => ({ ...x, postalCode: e.target.value }))} value={bankAddress.postalCode} />
                                <span className="text-danger">{getErrorMessageforField("BankAddress.PostalCode")}</span>
                            </div>
                            <div className="form-group col-12 col-md-4">
                                <label htmlFor="bankCity" className="fw-bold">City</label>
                                <input type="text" name="bankCity" required className="form-control" onChange={e => setBankAddress(x => ({ ...x, city: e.target.value }))} value={bankAddress.city} />
                                <span className="text-danger">{getErrorMessageforField("BankAddress.City")}</span>
                            </div>
                            <div className="form-group col-12 col-md-4">
                                <label htmlFor="bankCountry" className="fw-bold">Country</label>
                                <select id="bankCountry" required name="bankCountry" className="form-control" onChange={e => setBankAddress(x => ({ ...x, countryId: Number(e.target.value) }))} defaultValue={bankAddress.countryId}>
                                    <option key={"countryBank_None"} disabled value={-1}>Please select ...</option>
                                    {
                                        countries.map(entry => {
                                            return (
                                                <option key={"countryBank" + entry.value} value={entry.value}>
                                                    {entry.displayValue}
                                                </option>
                                            )
                                        })
                                    }
                                </select>
                                <span className="text-danger">{getErrorMessageforField("BankAddress.Country")}</span>
                            </div>
                        </>
                    )}

                    <hr />

                    { /* Account Holder Address Data */}

                    <div className="form-group col-12">
                        <label htmlFor="accHolderName" className="fw-bold">Account Holder</label>
                        <input type="text" id="accHolderName" name="accHolderName" className="form-control" onChange={e => setElvData(x => ({ ...x, accountHolderName: e.target.value }))} value={elvData.accountHolderName} required />
                        <span className="text-danger">{getErrorMessageforField("AccountHolderName")}</span>
                    </div>

                    <div className="form-group col-12">
                        <label htmlFor="accHolderBankStreet" className="fw-bold">Street</label>
                        <input type="text" id="accHolderBankStreet" name="accHolderBankStreet" className="form-control" onChange={e => setElvData(x => ({ ...x, accountHolderAddress: { ...x.accountHolderAddress, street: e.target.value } }))} value={elvData.accountHolderAddress.street} required />
                        <span className="text-danger">{getErrorMessageforField("AccountHolderAddress.Street")}</span>
                    </div>
                    <div className="form-group col-12 col-md-4">
                        <label htmlFor="acHolderPostalCode" className="fw-bold">Postal Code</label>
                        <input type="text" name="acHolderPostalCode" id="acHolderPostalCode" className="form-control" onChange={e => setElvData(x => ({ ...x, accountHolderAddress: { ...x.accountHolderAddress, postalCode: e.target.value } }))} value={elvData.accountHolderAddress.postalCode} required />
                        <span className="text-danger">{getErrorMessageforField("AccountHolderAddress.PostalCode")}</span>
                    </div>
                    <div className="form-group col-12 col-md-4">
                        <label htmlFor="accHolderCity" className="fw-bold">City</label>
                        <input type="text" id="accHolderCity" name="accHolderCity" className="form-control" onChange={e => setElvData(x => ({ ...x, accountHolderAddress: { ...x.accountHolderAddress, city: e.target.value } }))} value={elvData.accountHolderAddress.city} required />
                        <span className="text-danger">{getErrorMessageforField("AccountHolderAddress.City")}</span>
                    </div>
                    <div className="form-group col-12 col-md-4">
                        <label htmlFor="AccHolderBankCountry" className="fw-bold">Country</label>
                        <select id="AccHolderBankCountry" name="AccHolderBankCountry" className="form-control" onChange={e => setElvData(x => ({ ...x, accountHolderAddress: { ...x.accountHolderAddress, countryId: Number(e.target.value) } }))} defaultValue={elvData.accountHolderAddress.countryId}>
                            <option key={"countryAccHolder_None"} value={-1} disabled>Please select ...</option>
                            {
                                countries.map(entry => {
                                    return (
                                        <option key={"countryAccHolder" + entry.value} value={entry.value}>
                                            {entry.displayValue}
                                        </option>
                                    )
                                })
                            }
                        </select>
                        <span className="text-danger">{getErrorMessageforField("AccountHolderAddress.Country")}</span>
                    </div>

                    <hr />

                    <div className="form-group col-12">
                        <button type="submit" className="btn btn-primary w-100"><b>Next Step:</b> Receipts Upload<i className="bi bi-arrow-right ms-1" /></button>
                    </div>

                </div>
            </form>
        )
    }

    /**
     * Method look up error messages for specified Field-Name and returns 
     * error message to method call.
     * @param fieldName: Name of the field
     * @returns: Error message to render
     */
    function getErrorMessageforField(fieldName: string): string {

        if (fieldErrors && fieldName in fieldErrors) // Check if FieldName exists within errorObject | Fetch error message ...
        {
            return fieldErrors[fieldName].at(0) ?? "";
        }
        return ""; // Default Return value in case no error has been found ....
    }

    useEffect(() => {
        /* Fetch data for the specified claimGUID from the api */
        authRestClient.get<Claim>("/api/travelclaim/" + claimGuid)
            .then(response => {
                if (response.status === 200)
                {
                    setClaim(response.data);
                    elvData.claimId = response.data.guid;
                    /* Load country autocomplete */
                    restClient.get<CountryAutoComplete[]>("/api/countries/autocomplete")
                        .then(response2 => {
                            setCountries(response2.data);
                        })
                        .catch(err => { 
                        })
                }
            })
            .catch(err => { 
            })
    }, [])

    /* JSX Element that represents the current page */
    return (
        <main className="container text-center mb-5">

            <div className="row">
                <div className="col-12">
                    <img src="/brand.png" alt="brand" className="brand" />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <h2>{formState.currentStep === 1 ? "Account Information" : "Supporting Documents Upload"}</h2>
                </div>
                <div className="col-12">
                    <p>{formState.currentStep === 1 ? "Form to enter the account information to transfer the approved travel expenses." : ""}</p>
                </div>
            </div>

            {(formState.error !== "") && (
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-danger fw-bold">
                            {formState.error}
                        </div>
                    </div>
                </div>
            )}

            <div className="row">

                <div className="col-12 col-md-7 order-2 order-md-1">
                    <div className="card">
                        <div className="card-header fw-bold text-muted">
                            {formState.currentStep === 1 ? "Account Information" : "Supporting Documents Upload"}
                        </div>
                        <div className="card-body text-start">

                            {formState.currentStep === 1 ? renderHTMLForm() : renderHTMLFileUpload()}

                        </div>
                    </div>
                </div>

                { /* Show previously entered travel costs */ }
                <div className="col-12 col-md-5 order-1 order-md-2">
                    <div className="card">
                        <div className="card-header fw-bold text-muted">
                            Unit Costs
                        </div>
                        <div className="card-body text-start">

                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-arrow-right me-2"></i>Your place of employment</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.institution?.name} - {claim?.institution?.address.street} - {claim?.institution?.address.city}
                                </dd>
                            </dl>
                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-arrow-left me-2"></i>Event Location</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.event?.location}
                                </dd>
                            </dl>
                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-map me-2"></i>Distance</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {Math.round(claim?.calculatedTravelCosts?.distance ?? 0)} km
                                </dd>
                            </dl>
                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className={claim?.calculatedTravelCosts?.travelType === 0 ? "bi bi-airplane me-2" : "bi bi-train-front me-2"}></i>Transportation Type</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.calculatedTravelCosts?.travelType === 0 ? "Plane or combined" : "Rail Travel"}
                                </dd>
                            </dl>
                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label>Travel contribution</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    &euro; {claim?.calculatedTravelCosts?.amount} 
                                </dd>
                            </dl>

                            <hr />

                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-house me-2"></i>Nights Spent</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.calculatedAccommodationCosts?.nights ?? 0}
                                </dd>
                            </dl>
                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label>Amount</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    &euro; {claim?.calculatedAccommodationCosts?.amount ?? 0}
                                </dd>
                            </dl>

                        </div>
                    </div>
                </div>

            </div>

        </main>
    )
}
export default PostBankDetails;